/**
 * @module ClickTextInput
 */

import React, { useRef, useEffect } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import styles from "./ClickTextInput.module.scss";
import ClickTooltip from "../ClickTooltip";
import {
  Field as FormikField,
  ErrorMessage as FormikErrorMessage,
} from "formik";
import { applyRef } from "core";

/**
 * @description Component used in forms to render text inputs
 * @param {Object} props - All props of this Component
 * @param {string} [props.id] - DOM id of the text input element. Needed for acessibility.
 * @param {string} [props.name] - DOM name of the text input element. Needed if using withFormik.
 * @param {string} [props.value] - Current value of the input element.
 *
 * @param {function} [props.onChange] - callback called when the user inputs any data.
 * @param {function} [props.onBlur] - callback called when the user focus leaves the input.
 *
 * @param {string} [props.label] - the label that should be rendered with this input
 * @param {string} [props.ariaLabel] - DOM aria-label to be used on the input element.
 * @param {string} [props.placeholder] - placeholder to be shown while the input is empty.
 * @param {string} [props.helpText] - Text with instructions of how to input the data for this input.
 * @param {string} [props.tooltip] - Text shown when the user hovers this element.
 * @param {string} [props.maxLength] - Max length of text input string.
 *
 * @param {string} [props.className] - CSS classes for this input container
 * @param {bool} [props.noStyle=false] - If true, renders the input without styling (as a <span>)
 * @param {bool} [props.inlineStyle=false] - If true, renders the input with inline editing styling
 * @param {bool} [props.dark=false] - If true, renders the input with light foreground, to be used in dark backgrounds
 * @param {bool} [props.autoResize=false] - If true, this input's width changes as the user inputs it's text
 *
 * @param {bool} [props.withFormik=false] - If true, use data and callbacks from formik to control this input.
 * @param {function} [props.validate] - Callback used to validate this form field: val => errorMessage.
 * @memberof module:ClickTextInput
 */
const ClickTextInput = React.forwardRef(
  ({ name, validate, withFormik, ...otherProps }, ref) => {
    let inputElement;
    if (withFormik) {
      inputElement = (
        <div>
          <FormikField
            name={name}
            validate={validate}
            render={({ field /*, form */ }) => (
              <RenderInput {...otherProps} ref={ref} {...field} />
            )}
          />
          <div className={styles.errorMessage}>
            <FormikErrorMessage name={name} />
          </div>
        </div>
      );
    } else {
      inputElement = <RenderInput name={name} {...otherProps} ref={ref} />;
    }
    return inputElement;
  },
);

const RenderInput = React.forwardRef(
  (
    {
      id,
      name,
      value,
      onChange,
      onBlur,
      onFocus,
      label,
      ariaLabel,
      placeholder,
      helpText,
      tooltip,
      maxLength,
      className,
      noStyle,
      inlineStyle,
      dark,
      autoResize,
    },
    ref,
  ) => {
    const inputRef = useRef();

    // if autoResize, update width when the placeholder or value prop changes
    useEffect(() => {
      if (autoResize) {
        inputRef.current.style.width = "1px";
        if (value !== inputRef.current.value) {
          inputRef.current.value = value;
        }
        const shouldReset = inputRef.current.value === "";
        if (inputRef.current.value === "") {
          inputRef.current.value = placeholder;
        }
        inputRef.current.style.width = `${inputRef.current.scrollWidth}px`;
        if (shouldReset) {
          inputRef.current.value = "";
        }
      }
    }, [autoResize, value, placeholder]);

    // resize the component after each keystroke (if autoResize is set)
    const handleInput = () => {
      inputRef.current.style.width = "1px";
      const shouldReset = inputRef.current.value === "";
      if (inputRef.current.value === "") {
        inputRef.current.value = placeholder;
      }
      inputRef.current.style.width = `${inputRef.current.scrollWidth}px`;
      if (shouldReset) {
        inputRef.current.value = "";
      }
    };

    let inputElement = (tooltipRef) => (
      <div className={styles.textInputWrapper}>
        <input
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onInput={autoResize ? handleInput : undefined}
          type="text"
          placeholder={placeholder}
          aria-label={ariaLabel}
          className={classNames(styles.textInput, {
            [styles.noStyle]: noStyle === true,
            [styles.inlineStyle]: inlineStyle === true,
          })}
          aria-describedby={!!helpText ? `${id}-helptext` : undefined}
          ref={(element) => {
            applyRef(element, inputRef);
            applyRef(element, ref);
            applyRef(element, tooltipRef);
          }}
          maxLength={maxLength}
          spellCheck={false}
        />
      </div>
    );

    if (!tooltip && !label && !!ariaLabel) {
      tooltip = { content: ariaLabel };
    }

    const inputElementWithTooltip = !!tooltip && (
      <ClickTooltip {...tooltip}>
        {(tooltipRef) => inputElement(tooltipRef)}
      </ClickTooltip>
    );

    return (
      <div
        className={classNames(styles.textInputContainer, className, {
          [styles.dark]: dark,
        })}
      >
        {!!label && <label htmlFor={id}>{label}</label>}
        {!!helpText && (
          <p id={`${id}-helptext`} className={styles.helpText}>
            {helpText}
          </p>
        )}
        {tooltip ? inputElementWithTooltip : inputElement(undefined)}
      </div>
    );
  },
);

ClickTextInput.defaultProps = {
  noStyle: false,
  inlineStyle: false,
  dark: false,
  autoResize: false,
  withFormik: false,
};

ClickTextInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.any,

  onChange: PropTypes.func,
  onBlur: PropTypes.func,

  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  tooltip: PropTypes.shape({
    placement: PropTypes.oneOf(["top", "bottom", "left", "right"]),
    content: PropTypes.node,
  }),
  maxLength: PropTypes.number,
  className: PropTypes.string,
  noStyle: PropTypes.bool.isRequired,
  inlineStyle: PropTypes.bool.isRequired,
  dark: PropTypes.bool.isRequired,
  autoResize: PropTypes.bool.isRequired,

  withFormik: PropTypes.bool.isRequired,
  validate: PropTypes.func,
};

export default ClickTextInput;
